import React, { useEffect, useMemo, useState } from "react";
import { InlineMath } from "react-katex";
import { KernelConfig } from "../types/lenia";

type Props = {
  kernels: KernelConfig[];
  selectedKernel: KernelConfig | null;
  onSelect: (k: KernelConfig) => void;
};

function arraysEqual(a: number[], b: number[]) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** Kernels that differ only by kSize/gridSize/tMax/tag, keeping funcK/μK/σK/β identical. */
function groupVariants(kernels: KernelConfig[], basis: KernelConfig | null) {
  if (!basis) return [];
  return kernels.filter(
    (k) =>
      k.funcK === basis.funcK &&
      k.muK === basis.muK &&
      k.sigmaK === basis.sigmaK &&
      arraysEqual(k.betas, basis.betas)
  );
}

/** LaTeX for the kernel formula (use InlineMath when it starts with "K("). */
function kernelFormulaOf(kernel: KernelConfig): string {
  switch (kernel.funcK) {
    case "quad4":
      return String.raw`K(r) = \bigl(4r(1-r)\bigr)^{4}`;
    case "exp":
      return String.raw`K(r) = \exp\!\left(4-\frac{1}{r(1-r)}\right)`;
    case "gauss":
      return String.raw`K(r;\,\mu_k,\sigma_k) = \exp\!\left(-\frac{(r-\mu_k)^{2}}{2\sigma_k^{2}}\right)`;
    default:
      return `${kernel.funcK}`;
  }
}

export const KernelTopBar: React.FC<Props> = ({ kernels, selectedKernel, onSelect }) => {
  const variants = useMemo(() => groupVariants(kernels, selectedKernel), [kernels, selectedKernel]);

  // --- Option lists ---
  const rOptions = useMemo(
    () => Array.from(new Set(variants.map((k) => k.kSize))).sort((a, b) => b - a),
    [variants]
  );

  // Local state (required numbers)
  const [rValue, setRValue] = useState<number>(() => rOptions[0] ?? 0);
  const [gridValue, setGridValue] = useState<number>(0);
  const [tMaxValue, setTMaxValue] = useState<number>(0);

  // Options depending on current selections
  const gridOptionsForR = useMemo(() => {
    return Array.from(
      new Set(variants.filter((k) => k.kSize === rValue).map((k) => k.gridSize))
    ).sort((a, b) => b - a);
  }, [variants, rValue]);

  const tMaxOptionsForRGrid = useMemo(() => {
    return Array.from(
      new Set(
        variants
          .filter((k) => k.kSize === rValue && k.gridSize === gridValue)
          .map((k) => k.tMax)
      )
    ).sort((a, b) => b - a);
  }, [variants, rValue, gridValue]);

  useEffect(() => {
    console.log('rOptions:', rOptions);
    console.log('gridOptionsForR:', gridOptionsForR);
    console.log('tMaxOptionsForRGrid:', tMaxOptionsForRGrid);
  }, [rOptions, gridOptionsForR, tMaxOptionsForRGrid]);


  // Sync from external selection
  useEffect(() => {
    if (!selectedKernel) return;
    setRValue(selectedKernel.kSize);
    setGridValue(selectedKernel.gridSize);
    setTMaxValue(selectedKernel.tMax);
  }, [selectedKernel]);

  // Priority 1: when R changes, default gridSize then tMax
  useEffect(() => {
    if (gridOptionsForR.length === 0) return;
    // snap grid if invalid
    const nextGrid = gridOptionsForR.includes(gridValue) ? gridValue : gridOptionsForR[0];
    if (nextGrid !== gridValue) setGridValue(nextGrid);

    // now compute tMax options for (R, nextGrid)
    const nextTMaxOptions = Array.from(
      new Set(
        variants
          .filter((k) => k.kSize === rValue && k.gridSize === nextGrid)
          .map((k) => k.tMax)
      )
    ).sort((a, b) => b - a);

    if (nextTMaxOptions.length > 0 && !nextTMaxOptions.includes(tMaxValue)) {
      setTMaxValue(nextTMaxOptions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rValue, gridOptionsForR.join(","), variants]);

  // Priority 2: when grid changes (but R the same), default tMax
  useEffect(() => {
    if (tMaxOptionsForRGrid.length === 0) return;
    if (!tMaxOptionsForRGrid.includes(tMaxValue)) {
      setTMaxValue(tMaxOptionsForRGrid[0]);
    }
  }, [tMaxOptionsForRGrid, tMaxValue]);

  // Exact variant selection
  const trySelect = (r: number, grid: number, tMax: number) => {
    if (!selectedKernel) return;
    const exact = variants.find(
      (k) => k.kSize === r && k.gridSize === grid && k.tMax === tMax
    );
    if (exact) onSelect(exact);
  };

  if (!selectedKernel) return null;

  const kernelFormula = kernelFormulaOf(selectedKernel);

  return (
    <div
      className="kernel-topbar"
      style={{
        padding: "0 0 0.5rem 0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          flex: 1,
          textAlign: "center",
          fontSize: "1rem",
          color: "#94a3b8",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
          display: "flex",
          gap: "0.5rem",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        {kernelFormula.startsWith("K(") ? <InlineMath math={kernelFormula} /> : kernelFormula}

        {selectedKernel.funcK === "gauss" && (
          <>
            {" · μₖ="}
            {selectedKernel.muK}
            {" · σₖ="}
            {selectedKernel.sigmaK}
          </>
        )}

        {" · β=["}
        {selectedKernel.betas.join(",")}
        {"] · R="}

        {/* R selector (primary) */}
        <select
          value={String(rValue)}
          onChange={(e) => {
            const nextR = Number(e.target.value);
            // Decide next grid & tMax immediately to keep UI snappy
            const nextGridOptions = Array.from(
              new Set(variants.filter((k) => k.kSize === nextR).map((k) => k.gridSize))
            ).sort((a, b) => b - a);
            const nextGrid = nextGridOptions[0] ?? gridValue;

            const nextTOptions = Array.from(
              new Set(
                variants
                  .filter((k) => k.kSize === nextR && k.gridSize === nextGrid)
                  .map((k) => k.tMax)
              )
            ).sort((a, b) => b - a);
            const nextT = nextTOptions[0] ?? tMaxValue;

            setRValue(nextR);
            setGridValue(nextGrid);
            setTMaxValue(nextT);
            trySelect(nextR, nextGrid, nextT);
          }}
          style={dropdownStyle}
          aria-label="Kernel R"
          title="Kernel R"
        >
          {rOptions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>

        {" · grid="}

        {/* gridSize selector (secondary) */}
        <select
          value={String(gridValue)}
          onChange={(e) => {
            const nextGrid = Number(e.target.value);
            const nextTOptions = Array.from(
              new Set(
                variants
                  .filter((k) => k.kSize === rValue && k.gridSize === nextGrid)
                  .map((k) => k.tMax)
              )
            ).sort((a, b) => b - a);
            const nextT = nextTOptions[0] ?? tMaxValue;

            setGridValue(nextGrid);
            setTMaxValue(nextT);
            trySelect(rValue, nextGrid, nextT);
          }}
          style={dropdownStyle}
          aria-label="Kernel grid size"
          title="Kernel grid size"
        >
          {gridOptionsForR.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>

        {" · "}
        <span>tₘₐₓ=</span>

        {/* tMax selector (tertiary) */}
        <select
          value={String(tMaxValue)}
          onChange={(e) => {
            const nextT = Number(e.target.value);
            setTMaxValue(nextT);
            trySelect(rValue, gridValue, nextT);
          }}
          style={dropdownStyle}
          aria-label="Kernel t_max"
          title="Kernel t_max"
        >
          {tMaxOptionsForRGrid.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

const dropdownStyle: React.CSSProperties = {
  background: "rgba(15,23,42,0.6)",
  color: "#e2e8f0",
  border: "1px solid #334155",
  borderRadius: "0.375rem",
  padding: "0.125rem 0.5rem",
  fontSize: "0.95rem",
  cursor: "pointer",
  outline: "none",
  appearance: "none",
};
